/**
 * Realtime Provider
 *
 * @module core/realtime
 *
 * @description
 * Context provider para conexión WebSocket compartida.
 * Maneja reconexión automática y estado de conexión.
 *
 * @remarks
 * **Modo Offline-Ready**:
 * - El sistema funciona completamente sin WebSocket conectado
 * - La conexión es opcional y se intenta solo si `enabled=true`
 * - Todos los componentes reciben `isConnected=false` cuando no hay conexión
 * - No se muestran errores al usuario si el servidor no está disponible
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import type { RealtimeContextValue, ConnectionStatus, RealtimeProviderOptions } from './types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

/**
 * Valor por defecto para contexto (modo offline)
 */
const offlineContextValue: RealtimeContextValue = {
  socket: null,
  status: 'disconnected',
  isConnected: false,
  error: null,
  reconnect: () => {
    console.debug('[Realtime] Reconnect called but realtime is disabled');
  },
  disconnect: () => {
    console.debug('[Realtime] Disconnect called but realtime is disabled');
  },
};

/**
 * Context para realtime
 */
const RealtimeContext = createContext<RealtimeContextValue>(offlineContextValue);

/**
 * Props del provider
 */
interface RealtimeProviderProps extends RealtimeProviderOptions {
  children: React.ReactNode;
  projectId: string;
  /**
   * Habilitar conexión WebSocket
   * @default true
   * @remarks Si es false, el provider funciona en modo offline
   */
  enabled?: boolean;
}

/**
 * Provider de WebSocket para tiempo real
 *
 * @remarks
 * - Si `enabled=false`, el provider no intenta conectar
 * - Si el servidor no responde, el sistema sigue funcionando en modo offline
 * - Errores de conexión se loguean pero no se propagan al usuario
 */
export function RealtimeProvider({
  children,
  projectId,
  serverUrl = API_URL,
  namespace = '/realtime',
  autoConnect = true,
  reconnectionAttempts = 3, // Reducido para no bloquear mucho tiempo
  enabled = true,
}: RealtimeProviderProps) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [error, setError] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const connectionAttempted = useRef(false);

  /**
   * Inicializar socket
   */
  const initSocket = useCallback(() => {
    // No conectar si está deshabilitado
    if (!enabled) {
      console.debug('[Realtime] WebSocket disabled, running in offline mode');
      return null;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    // Obtener token de auth
    let token: string | null = null;
    const authStore = localStorage.getItem('auth-storage');
    if (authStore) {
      try {
        const parsed = JSON.parse(authStore);
        token = parsed.state?.token;
      } catch (e) {
        console.debug('[Realtime] No auth token available');
      }
    }

    const newSocket = io(`${serverUrl}${namespace}`, {
      query: { projectId },
      auth: token ? { token } : undefined,
      reconnection: true,
      reconnectionAttempts,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      autoConnect,
      timeout: 5000, // Timeout corto para no bloquear
    });

    // Event handlers
    newSocket.on('connect', () => {
      console.debug('[Realtime] Connected to server');
      setStatus('connected');
      setError(null);
    });

    newSocket.on('disconnect', (reason) => {
      console.debug('[Realtime] Disconnected:', reason);
      setStatus('disconnected');
    });

    newSocket.on('connect_error', (err) => {
      // Solo loguear en debug, no mostrar error al usuario
      console.debug('[Realtime] Connection error (offline mode active):', err.message);
      setStatus('disconnected'); // Usar 'disconnected' en lugar de 'error' para UX limpio
      // No setear error visible - el sistema funciona sin conexión
    });

    newSocket.on('reconnect_attempt', (attempt) => {
      console.debug(`[Realtime] Reconnection attempt ${attempt}/${reconnectionAttempts}`);
      setStatus('connecting');
    });

    newSocket.on('reconnect', () => {
      console.debug('[Realtime] Reconnected');
      setStatus('connected');
      setError(null);
    });

    newSocket.on('reconnect_failed', () => {
      console.debug('[Realtime] Reconnection failed, continuing in offline mode');
      setStatus('disconnected');
      // No setear error - el sistema funciona sin conexión
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    return newSocket;
  }, [serverUrl, namespace, projectId, autoConnect, reconnectionAttempts, enabled]);

  /**
   * Conectar al montar (si está habilitado)
   */
  useEffect(() => {
    if (!projectId || !enabled) {
      setStatus('disconnected');
      return;
    }

    // Evitar múltiples intentos de conexión
    if (connectionAttempted.current) return;
    connectionAttempted.current = true;

    setStatus('connecting');
    const newSocket = initSocket();

    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
      socketRef.current = null;
      connectionAttempted.current = false;
    };
  }, [projectId, enabled, initSocket]);

  /**
   * Reconectar manualmente
   */
  const reconnect = useCallback(() => {
    if (!enabled) {
      console.debug('[Realtime] Cannot reconnect - realtime is disabled');
      return;
    }

    if (socketRef.current) {
      socketRef.current.connect();
    } else {
      connectionAttempted.current = false;
      initSocket();
    }
  }, [initSocket, enabled]);

  /**
   * Desconectar
   */
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
  }, []);

  const value: RealtimeContextValue = {
    socket,
    status,
    isConnected: status === 'connected',
    error,
    reconnect,
    disconnect,
  };

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

/**
 * Hook para acceder al contexto de realtime
 *
 * @remarks
 * A diferencia de otros hooks de contexto, este NO lanza error si se usa
 * fuera del provider. Retorna el valor offline por defecto.
 * Esto permite que los componentes funcionen sin WebSocket.
 */
export function useRealtime(): RealtimeContextValue {
  const context = useContext(RealtimeContext);
  // Retornar valor offline si no hay contexto (graceful degradation)
  return context || offlineContextValue;
}

/**
 * Hook para verificar si el contexto está disponible y conectado
 */
export function useRealtimeOptional(): RealtimeContextValue {
  return useContext(RealtimeContext) || offlineContextValue;
}

/**
 * Hook para verificar si realtime está disponible
 */
export function useIsRealtimeAvailable(): boolean {
  const { isConnected } = useRealtime();
  return isConnected;
}
